/**
 * BoothLauncher — iter190 ✦ Ambient Participation Refinement
 *
 * Dissolves the "boxed" feel from iter189 without removing any
 * structure. Five refinements:
 *
 *   1. BORDERS DISSOLVED — default cards lose their hard outline.
 *      Visual separation comes from spacing, the soft icon pad, and
 *      a hairline-only top edge on hover. Dimmed cards keep a faint
 *      gold contour (so the locked Archive products still read as
 *      a distinct visual class — never confused with active cards).
 *
 *   2. TYPOGRAPHY HIERARCHY (3 tiers):
 *        functional — Upload, Memory Snap, Pocket Booth, Spin (quieter)
 *        intimate   — Lens Notes, Drop One Word, Audio/Video, Flip
 *                     Guestbook (calm, gold-italic helper emphasis)
 *        archival   — Film, Highlight Reel (cinematic destination
 *                     panels: nearly full-width, larger type, slower
 *                     motion, walnut-fade poster background)
 *
 *   3. FILM / HIGHLIGHT REEL = destination panels.
 *      Wider footprint (negative side padding bleeds into the page),
 *      poster-like aspect, deeper walnut radial vignette, larger
 *      typography, slower drift. They emotionally conclude the OS.
 *
 *   4. HERO ATMOSPHERE — a single soft gold radial behind the
 *      heading drifts very slowly. Pure CSS, GPU-friendly.
 *
 *   5. MEMORY WALL LIVE ENERGY — the Memory Wall card now carries
 *      a tiny live thumbnail strip (last 3 photos) and a quiet
 *      "N moments added recently ✦" microcopy line that softly
 *      pulses on update.
 *
 * No new products. No padlocks. No neon. No parallax. All animation
 * is CSS transform + opacity. Tablet-safe.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BOOTH_THEMES } from '../../lib/boothOSThemes';
import {
  SERIF,
  Eyebrow, Heading, Lede,
} from './boothShellAtoms';
import {
  getBoothOSGuestActions,
  BOOTH_OS_INLINE_FLOWS,
  ARCHIVE_LOCKED_COPY,
} from '../../shared/packageGuestProducts';
import { useEngagement } from '../../lib/features';
import { getMediaUrl } from '../../lib/media';
import BoothSection from './BoothSection';
import BoothAtmosphereRibbon from './BoothAtmosphereRibbon';
import useScrollRestore from '../../shared/navigation/useScrollRestore';
import useArrivalReveal from '../../shared/navigation/useArrivalReveal';

const LEAVE_HOLD_MS = 1500;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Visual tier per product. Drives the typographic + chrome register
// inside LauncherCard. Lives here (not in the matrix) because it is
// a *visual* concern — the matrix stays semantic.
const CARD_TIER = {
  // functional — quieter, smaller titles
  qr_upload:        'functional',
  memory_snap:      'functional',
  pocket_booth:     'functional',
  spin_film_360:    'functional',
  // intimate — emotional centre register
  video_guestbook:  'intimate',
  memory_guestbook: 'intimate',
  lens_notes:       'intimate',
  drop_one_word:    'intimate',
  // see the night forming — single live card
  memory_wall_card: 'living',
  // archival destination panels
  full_event_film:  'archival',
  highlight_reel:   'archival',
  // iter200b — The Final Night ✦ joins the archival register; it is
  // the editorial destination of "Preserve the night ✦".
  final_night:      'archival',
};

const BoothLauncher = ({ theme, event, onCardPress, onOpenThemePicker }) => {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [notice, setNotice] = useState('');
  const { counts } = useEngagement(event?.event_id, axios, API);

  // iter197 — Universal back-nav cinematic continuity.
  // Restore the launcher's scroll position so coming back from a
  // capture surface lands the guest at the exact card they tapped.
  useScrollRestore(event?.event_id ? `booth_launcher_${event.event_id}` : null);
  // Skip the arrival / staggered-reveal animation if the guest has
  // already seen the room compose itself this session — back must
  // feel instantaneous, not like a route reload.
  const arrivalReveal = useArrivalReveal(event?.event_id ? `booth_launcher_${event.event_id}` : null);

  // Memory Wall live thumbnail strip — last 3 photos, refreshed
  // every 18s. Quiet — no notifications, no badges. Fails open.
  const [recentPhotos, setRecentPhotos] = useState([]);
  useEffect(() => {
    if (!event?.event_id) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/photos?limit=3`);
        if (!cancelled && Array.isArray(r.data)) {
          setRecentPhotos(r.data.slice(0, 3));
        }
      } catch { /* fail open */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 18000);
    return () => { cancelled = true; clearInterval(id); };
  }, [event?.event_id]);

  // iter191 — Drifting word ✦. Pulls the latest Drop One Word
  // submission every 22s. Used in two places: the hero glow (a
  // single quiet word floats below the heading) and the atmosphere
  // ribbon (occasionally takes over for ~6s with the emotional line
  // "'hopeful' just drifted into the room ✦").
  const [latestWord, setLatestWord] = useState(null);
  useEffect(() => {
    if (!event?.event_id && !event?.slug) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/one-word/event/${event.slug || event.event_id}/summary`);
        if (!cancelled && r.data?.latest && r.data.latest.length > 0) {
          setLatestWord(r.data.latest[0]?.word || null);
        }
      } catch { /* fail open */ }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 22000);
    return () => { cancelled = true; clearInterval(id); };
  }, [event?.event_id, event?.slug]);

  // iter209 ✦ — Guest Booth OS simplification.
  // We render a flat 6-action participation list, not the iter189
  // 4-section taxonomy. Sections taught system architecture; the
  // flat list teaches participation. Operator surfaces (Event
  // Dashboard / Archive / What Remains) still consume the sectioned
  // taxonomy via getBoothOSSections — only the guest entrance
  // simplifies here.
  const actions = getBoothOSGuestActions(event);

  // iter214 ✦ — Multi-upload. Production rehearsal: a guest with
  // five photos on their phone had to tap "Upload Memories" five
  // times. The native picker supports multi-select with `multiple`,
  // and our backend's photos endpoint accepts one file per POST,
  // so we iterate sequentially. Sequential (not parallel) keeps
  // ordering deterministic and avoids hammering the mobile network
  // stack with concurrent multipart uploads — one slow tower
  // doesn't time-out the others.
  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length || !event?.event_id) return;
    setUploading(true);
    let ok = 0;
    let failed = 0;
    try {
      for (let i = 0; i < fileList.length; i += 1) {
        const file = fileList[i];
        try {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API}/events/${event.event_id}/photos?source=booth_os`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          ok += 1;
          // Soft progress for multi-file batches — single uploads
          // keep the original "Saved into tonight ✦" beat.
          if (fileList.length > 1) {
            setNotice(`Saving ${ok} of ${fileList.length} ✦`);
          }
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) {
        setNotice(
          fileList.length === 1
            ? 'Saved into tonight ✦'
            : `${ok} memories saved into tonight ✦`,
        );
      } else if (ok === 0) {
        setNotice("The upload didn't quite land — please try again ✦");
      } else {
        // Partial — be honest but quiet.
        setNotice(`${ok} saved, ${failed} didn't land — try those again ✦`);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      setTimeout(() => setNotice(''), 3500);
    }
  };

  const handleCardPress = (product, dimmed) => {
    if (dimmed) return;
    const flowId = BOOTH_OS_INLINE_FLOWS[product.id];
    if (flowId) { onCardPress(flowId); return; }
    if (product.action === 'open-uploader') { fileRef.current?.click(); return; }
    if (typeof product.route === 'function') {
      // iter210 ✦ — every launch from the booth-os shell is by
      // definition a kiosk launch (a shared device passed between
      // guests). Append ?kiosk=1 so the standalone capture pages
      // (Lens Notes, Drop One Word, Pocket Booth, …) know to send
      // the guest to the Personal Memory Page in kiosk mode after
      // saving — surfacing the "Pass to the next memory ✦"
      // gesture for the next guest.
      const target = product.route(event.slug, event);
      const withKiosk = target.includes('?')
        ? `${target}&kiosk=1`
        : `${target}?kiosk=1`;
      navigate(withKiosk);
    }
  };

  return (
    <>
      <style>{`
        @keyframes boothBreathe {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.86; }
        }
        @keyframes boothPulse {
          0%, 100% { transform: scale(1);   opacity: 0.55; }
          50%      { transform: scale(1.4); opacity: 0.95; }
        }
        @keyframes boothCardFloat {
          0%, 100% { transform: translateY(0px); }
          50%      { transform: translateY(-1.5px); }
        }
        @keyframes boothArchivalDrift {
          0%   { background-position: 30% 30%; }
          50%  { background-position: 70% 70%; }
          100% { background-position: 30% 30%; }
        }
        @keyframes boothLensGlow {
          0%, 100% { box-shadow: 0 18px 40px rgba(0,0,0,0.35), 0 0 0px rgba(212,170,82,0.00); }
          50%      { box-shadow: 0 18px 40px rgba(0,0,0,0.35), 0 0 28px rgba(212,170,82,0.13); }
        }
        @keyframes boothHeroHaze {
          0%, 100% { transform: translate(-50%, -50%) scale(1);    opacity: 0.55; }
          50%      { transform: translate(-50%, -50%) scale(1.18); opacity: 0.80; }
        }
        @keyframes boothLiveCount {
          0%, 100% { opacity: 0.62; }
          50%      { opacity: 1.00; }
        }
        /* iter191 — arrival pause + drifting word + section stagger */
        @keyframes boothArrivalFade {
          0%   { opacity: 0; transform: scaleX(0.6); }
          100% { opacity: 1; transform: scaleX(1);   }
        }
        @keyframes boothWordDrift {
          0%   { opacity: 0; transform: translateY(8px); }
          18%  { opacity: 0.78; transform: translateY(0); }
          82%  { opacity: 0.78; transform: translateY(0); }
          100% { opacity: 0;    transform: translateY(-6px); }
        }
        @keyframes boothSectionReveal {
          0%   { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0);   }
        }
      `}</style>

      <div
        data-testid="booth-launcher"
        style={{
          minHeight: '100vh',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center',
          padding: 'clamp(40px, 6vw, 70px) clamp(20px, 5vw, 60px) clamp(72px, 8vw, 96px)',
          animation: arrivalReveal ? 'editorialFade .6s ease-out' : 'none',
        }}
      >
        {/* ── HERO ATMOSPHERE — single soft gold radial that drifts very
            slowly behind the heading. GPU-friendly, no parallax. ── */}
        <div
          data-testid="booth-hero-atmosphere"
          aria-hidden
          style={{
            position: 'absolute', top: 'clamp(120px, 14vw, 200px)', left: '50%',
            width: 720, height: 420,
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(ellipse at center, rgba(212,170,82,0.16) 0%, rgba(212,170,82,0.06) 35%, rgba(0,0,0,0) 70%)',
            animation: 'boothHeroHaze 16s ease-in-out infinite',
            pointerEvents: 'none', zIndex: 0,
            filter: 'blur(8px)',
          }}
        />

        <header style={{
          width: '100%', maxWidth: 720, textAlign: 'center',
          position: 'relative', zIndex: 1,
        }}>
          <Eyebrow theme={theme}>✦ {event.event_name ? event.event_name.toUpperCase() : 'TONIGHT'}</Eyebrow>
          <Heading theme={theme}>How will tonight be remembered? ✦</Heading>
          <Lede theme={theme}>
            Every memory lands quietly into the archive. Touch one to begin.
          </Lede>
          {notice && (
            <p
              data-testid="booth-launcher-notice"
              style={{
                margin: '20px auto 0', maxWidth: 420,
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 14,
                color: theme.inkMuted,
              }}
            >
              {notice}
            </p>
          )}

          {/* iter191 — Drifting word ✦. A single quiet word from the
              room floats below the heading and dissolves. Only when
              the night has begun (someone has dropped a word) — keeps
              the arrival pause anchored to real human voices, not a
              placeholder. */}
          {latestWord && (
            <p
              data-testid="booth-hero-drifting-word"
              key={latestWord}
              style={{
                margin: '26px auto 0', fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 'clamp(15px, 1.6vw, 18px)',
                color: theme.gold, opacity: 0.78, letterSpacing: '0.015em',
                animation: 'boothWordDrift 9s ease-in-out both',
              }}
            >
              “{latestWord}” just drifted in ✦
            </p>
          )}
        </header>

        {/* iter191 — ARRIVAL PAUSE. A soft horizontal gold thread
            below the hero gives the eye a breath between "where you
            are" and "what to touch". Restrained — single hairline,
            short, faded ends. No copy. Just breath. */}
        <div
          data-testid="booth-arrival-pause"
          aria-hidden
          style={{
            width: 'min(220px, 60%)', height: 1,
            margin: 'clamp(48px, 7vw, 84px) auto clamp(20px, 3vw, 32px)',
            background: 'linear-gradient(90deg, rgba(212,170,82,0) 0%, rgba(212,170,82,0.45) 50%, rgba(212,170,82,0) 100%)',
            opacity: arrivalReveal ? 0 : 1,
            animation: arrivalReveal ? 'boothArrivalFade 1.4s ease-out 0.6s forwards' : 'none',
            position: 'relative', zIndex: 1,
          }}
        />

        <main
          data-testid="booth-launcher-grid"
          style={{
            width: '100%', maxWidth: 720,
            display: 'flex', flexDirection: 'column',
            gap: 'clamp(12px, 1.6vw, 18px)',
            margin: 'clamp(20px, 3vw, 36px) auto 0',
            position: 'relative', zIndex: 1,
          }}
        >
          {/* iter209 ✦ — flat 6-action participation list. The launcher
              no longer surfaces preservation / wall / projector /
              atmosphere to guests; those remain in the system but
              live on operator surfaces. Lens Notes carries `gravity:
              'centred'` so the LauncherCard gives it slightly larger
              type + slightly stronger ink contrast + slightly more
              breathing room — quiet emotional gravity, no badges. */}
          {actions.map((entry, idx) => (
            <div
              key={entry.product.id}
              style={{
                opacity: arrivalReveal ? 0 : 1,
                animation: arrivalReveal
                  ? `boothSectionReveal 760ms ease-out ${0.7 + idx * 0.12}s forwards`
                  : 'none',
                // Lens Notes gets a touch more breathing room above
                // and below — never an "icon glow" or "featured"
                // chrome, just space. Quiet gravity.
                marginTop:    entry.gravity === 'centred' ? 'clamp(6px, 0.9vw, 12px)' : 0,
                marginBottom: entry.gravity === 'centred' ? 'clamp(6px, 0.9vw, 12px)' : 0,
              }}
            >
              <LauncherCard
                theme={theme}
                product={entry.product}
                dimmed={false}
                dimmedCopy={null}
                tier={CARD_TIER[entry.product.id] || 'intimate'}
                archival={false}
                breathing={false}
                gravity={entry.gravity}
                helper={entry.helper}
                uploading={false}
                recentPhotos={null}
                liveCount={null}
                onPress={() => handleCardPress(entry.product, false)}
              />
            </div>
          ))}
        </main>

        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleUpload}
          data-testid="booth-launcher-upload-input"
        />

        <footer style={{
          marginTop: 'clamp(28px, 4vw, 44px)',
          display: 'flex', alignItems: 'center', gap: 18,
          flexWrap: 'wrap', justifyContent: 'center',
          position: 'relative', zIndex: 1,
        }}>
          <button
            type="button"
            onClick={onOpenThemePicker}
            data-testid="booth-theme-picker-open"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: theme.inkFaint, fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12.5, letterSpacing: '0.16em', textTransform: 'uppercase',
              padding: 6,
            }}
          >
            ✦ Atmosphere · {BOOTH_THEMES[theme.id].label}
          </button>
          <span style={{
            color: theme.inkFaint, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, letterSpacing: '0.04em',
          }}>·</span>
          <LeaveBoothButton theme={theme} />
        </footer>
      </div>

      <BoothAtmosphereRibbon theme={theme} counts={counts} latestWord={latestWord} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────
//  Leave Booth Mode — discreet long-press exit.
// ─────────────────────────────────────────────────────────────────
const LeaveBoothButton = ({ theme }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const startRef = useRef(0);

  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(1, elapsed / LEAVE_HOLD_MS);
    setProgress(p);
    if (p < 1) {
      timerRef.current = requestAnimationFrame(tick);
    } else {
      try {
        localStorage.removeItem('booth_os_paired_event_id');
        localStorage.removeItem('booth_os_paired_event_name');
      } catch {}
      navigate('/');
    }
  }, [navigate]);

  const begin = () => {
    startRef.current = Date.now();
    setProgress(0);
    timerRef.current = requestAnimationFrame(tick);
  };
  const cancel = () => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    timerRef.current = null;
    setProgress(0);
  };

  return (
    <button
      type="button"
      onPointerDown={begin}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      data-testid="booth-leave-longpress"
      aria-label="Hold to leave booth mode"
      style={{
        position: 'relative',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: theme.inkFaint, fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, letterSpacing: '0.04em', padding: '4px 8px',
      }}
    >
      EventDrop Booth OS ✦
      {progress > 0 && (
        <span style={{
          position: 'absolute', left: 0, right: 0, bottom: 0, height: 1,
          background: theme.gold,
          transform: `scaleX(${progress})`,
          transformOrigin: 'left center',
          transition: 'background 200ms ease',
        }} />
      )}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────
//  LauncherCard — three visual tiers (functional / intimate /
//  archival) on top of the iter188 dimmed-state contract.
// ─────────────────────────────────────────────────────────────────
const LauncherCard = ({
  theme, product, dimmed, dimmedCopy, tier, archival, breathing,
  gravity, helper, uploading, recentPhotos, liveCount, onPress,
}) => {
  const Icon = product.icon;

  // ── Per-tier visual config ──────────────────────────────────────
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.functional;

  // iter209 ✦ — Lens Notes "centred" gravity. Slightly larger title,
  // slightly stronger contrast on the helper + subtitle lines. No
  // badges, no animation, no glow, no icon swap — just typographic
  // quiet gravity so a guest glancing down the list naturally feels
  // Lens Notes as the deepest contribution available.
  const isCentred = gravity === 'centred';
  const titleSize    = isCentred ? 'clamp(21px, 1.94vw, 24.5px)' : cfg.titleSize;
  const titleOpacity = isCentred ? 1                              : (cfg.titleOpacity ?? 1);
  const helperOpacity = isCentred
    ? Math.min(1, (cfg.helperOpacity ?? 0.55) + 0.14)
    : cfg.helperOpacity;
  const subtitleOpacity = isCentred
    ? Math.min(1, (cfg.subtitleOpacity ?? 0.7) + 0.08)
    : cfg.subtitleOpacity;

  // Dimmed Archive-only products on Room keep a faint contour so they
  // remain visually distinct from active cards (no padlock language).
  const borderStyle = dimmed
    ? `1px solid rgba(212,170,82,0.18)`
    : 'none';

  const baseBg = dimmed
    ? 'rgba(20,12,6,0.30)'
    : archival
      ? 'radial-gradient(ellipse at 30% 25%, rgba(212,170,82,0.10) 0%, rgba(0,0,0,0) 55%), linear-gradient(135deg, rgba(15,8,3,0.92) 0%, rgba(34,21,10,0.78) 50%, rgba(12,6,2,0.95) 100%)'
      : 'transparent';

  const baseShadow = dimmed
    ? 'none'
    : archival
      ? '0 22px 60px rgba(0,0,0,0.50), inset 0 1px 0 rgba(212,170,82,0.10)'
      : breathing
        ? 'none' /* handled via boothLensGlow keyframes */
        : '0 6px 18px rgba(0,0,0,0.22)';

  const cardAnimation = dimmed
    ? 'none'
    : breathing
      ? 'boothLensGlow 5.5s ease-in-out infinite, boothCardFloat 8s ease-in-out infinite'
      : archival
        ? 'boothArchivalDrift 26s ease-in-out infinite, boothCardFloat 12s ease-in-out infinite'
        : 'boothCardFloat 9s ease-in-out infinite';

  // Archival cards bleed slightly wider for the destination-panel
  // feel — but only inside the bounded launcher column. Achieved
  // with a small negative inline margin (~14px each side).
  const wrapperStyle = archival
    ? { marginLeft: -14, marginRight: -14 }
    : null;

  const content = (
    <button
      type="button"
      onClick={onPress}
      disabled={dimmed}
      aria-disabled={dimmed}
      data-testid={`booth-card-${product.id}`}
      data-dimmed={dimmed ? '1' : '0'}
      data-archival={archival ? '1' : '0'}
      data-breathing={breathing ? '1' : '0'}
      data-tier={tier}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: archival ? 'flex-start' : 'center',
        gap: archival ? 22 : 16,
        width: '100%',
        minHeight: cfg.minHeight,
        padding: cfg.padding,
        borderRadius: archival ? 16 : 12,
        background: baseBg,
        backgroundSize: archival ? '200% 200%' : '100% 100%',
        border: borderStyle,
        cursor: dimmed ? 'default' : 'pointer',
        color: theme.ink,
        textAlign: 'left',
        opacity: dimmed ? 0.55 : 1,
        filter: dimmed ? 'blur(0.2px)' : 'none',
        transition: 'transform 320ms ease, background 280ms ease, box-shadow 320ms ease',
        boxShadow: baseShadow,
        willChange: 'transform',
        animation: cardAnimation,
      }}
      onPointerDown={(e) => { if (!dimmed) e.currentTarget.style.transform = 'translateY(1px)'; }}
      onPointerUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      onPointerLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {Icon && (
        <span
          aria-hidden
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: cfg.iconBox, height: cfg.iconBox,
            borderRadius: archival ? 10 : 8,
            background: dimmed
              ? 'rgba(212,170,82,0.08)'
              : archival
                ? 'rgba(212,170,82,0.18)'
                : 'rgba(212,170,82,0.10)',
            color: theme.gold,
            flexShrink: 0,
            marginTop: archival ? 6 : 0,
          }}
        >
          <Icon size={cfg.iconSize} weight="duotone" />
        </span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{
          margin: 0,
          fontFamily: SERIF, fontWeight: cfg.titleWeight,
          fontSize: titleSize,
          lineHeight: 1.18, letterSpacing: '-0.008em',
          color: theme.ink,
          opacity: titleOpacity,
        }}>
          {uploading ? 'Uploading ✦' : product.title}
        </h3>
        {helper && !dimmed && (
          <p
            data-testid={`booth-card-helper-${product.id}`}
            style={{
              margin: '5px 0 0', fontFamily: SERIF, fontStyle: 'italic',
              fontSize: cfg.helperSize,
              color: theme.gold, opacity: helperOpacity,
              lineHeight: 1.35, letterSpacing: '0.005em',
            }}
          >
            {helper}
          </p>
        )}
        <p style={{
          margin: '4px 0 0',
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: cfg.subtitleSize,
          color: theme.inkMuted, lineHeight: 1.45,
          opacity: subtitleOpacity,
        }}>
          {dimmed ? (dimmedCopy || ARCHIVE_LOCKED_COPY) : product.subtitle}
        </p>

        {/* Memory Wall — live thumbnail strip + ambient counter */}
        {!dimmed && product.id === 'memory_wall_card' && recentPhotos && recentPhotos.length > 0 && (
          <div
            data-testid="booth-card-memory-wall-strip"
            style={{
              marginTop: 14, display: 'flex', gap: 8, alignItems: 'center',
            }}
          >
            {recentPhotos.map((p, i) => (
              <span
                key={p.photo_id || p.id || i}
                aria-hidden
                style={{
                  width: 38, height: 38, borderRadius: 6,
                  background: `center/cover no-repeat url("${getMediaUrl(p.url || p.file_url || p.thumbnail_url || '')}"), rgba(212,170,82,0.10)`,
                  flexShrink: 0,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.40)',
                }}
              />
            ))}
          </div>
        )}
        {!dimmed && product.id === 'memory_wall_card' && (liveCount ?? 0) >= 1 && (
          <p
            data-testid="booth-card-memory-wall-livecount"
            style={{
              margin: '10px 0 0', fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12.5, color: theme.gold,
              animation: 'boothLiveCount 4.8s ease-in-out infinite',
              letterSpacing: '0.01em',
            }}
          >
            {liveCount === 1
              ? '1 moment added so far ✦'
              : `${liveCount} moments added so far ✦`}
          </p>
        )}
      </div>
    </button>
  );

  return wrapperStyle ? <div style={wrapperStyle}>{content}</div> : content;
};

// ──────────────────────────────────────────────────────────────────
// Per-tier visual configuration. Functional cards are quieter;
// intimate cards calmer with a stronger helper line; archival cards
// occupy a destination panel.
// ──────────────────────────────────────────────────────────────────
const TIER_CONFIG = {
  functional: {
    minHeight: 84,
    padding: '16px 18px',
    iconBox: 36, iconSize: 18,
    titleSize: 'clamp(15.5px, 1.4vw, 18px)',
    titleWeight: 400,
    titleOpacity: 0.82,
    helperSize: 11.5, helperOpacity: 0.52,
    subtitleSize: 12.5, subtitleOpacity: 0.68,
  },
  intimate: {
    minHeight: 106,
    padding: '22px 22px',
    iconBox: 44, iconSize: 22,
    titleSize: 'clamp(19px, 1.7vw, 22px)',
    titleWeight: 500,
    titleOpacity: 1,
    helperSize: 13.5,  helperOpacity: 0.86,
    subtitleSize: 13.5, subtitleOpacity: 0.86,
  },
  living: {
    minHeight: 112,
    padding: '22px 22px 22px',
    iconBox: 44, iconSize: 22,
    titleSize: 'clamp(19px, 1.7vw, 22px)',
    titleWeight: 500,
    titleOpacity: 1,
    helperSize: 13,    helperOpacity: 0.80,
    subtitleSize: 13.5, subtitleOpacity: 0.85,
  },
  archival: {
    minHeight: 156,
    padding: '34px 30px',
    iconBox: 52, iconSize: 26,
    titleSize: 'clamp(24px, 2.4vw, 30px)',
    titleWeight: 500,
    titleOpacity: 1,
    helperSize: 14.5,  helperOpacity: 0.88,
    subtitleSize: 14.5, subtitleOpacity: 0.90,
  },
};

export default BoothLauncher;
